import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const BG = "https://images.unsplash.com/photo-1593640408182-31c70c8268f5?auto=format&fit=crop&w=1200&q=60";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("expired")) {
      toast.info("Tu sesión terminó. Inicia sesión de nuevo.", { duration: 6000 });
      window.history.replaceState({}, "", "/login");
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success("Bienvenido");
      // Roles operativos de taller no ven el dashboard; van directo al Hospital.
      const roles = Array.isArray(data?.roles) && data.roles.length ? data.roles : (data?.role ? [data.role] : []);
      const isWorkshopOnly = roles.length > 0 && roles.every((r) => r === "technician" || r === "taller");
      navigate(isWorkshopOnly ? "/hospital" : "/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex relative items-end bg-zinc-900" style={{ backgroundImage: `url(${BG})`, backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-black/40" />
        <div className="relative z-10 p-10 text-white">
          <div className="inline-block px-3 py-1 border border-white/30 text-xs font-mono-data tracking-wider mb-6">SISTEMA INTERNO</div>
          <h1 className="font-heading text-5xl font-extrabold leading-[1.05] mb-3">Inventario<br/>de Gadgets &<br/>Cómputo</h1>
          <p className="text-white/80 max-w-md">Control total de stock, precios multi-tier y movimientos en tiempo real.</p>
        </div>
      </div>
      <div className="flex flex-col items-center justify-center p-8 relative">
        <div className="absolute top-6 right-6 left-6 lg:left-auto flex items-center justify-end gap-2 text-xs">
          <span className="text-zinc-500">¿Eres revendedor o distribuidor?</span>
          <Link
            to="/portal/login"
            data-testid="link-to-portal-login"
            className="font-semibold text-[#002FA7] hover:text-[#002277] underline-offset-4 hover:underline inline-flex items-center gap-1"
          >
            Entra al portal de clientes
            <span aria-hidden>→</span>
          </Link>
        </div>
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-2">INICIO DE SESIÓN</div>
            <h2 className="font-heading text-3xl font-bold tracking-tight">Accede a tu panel</h2>
            <p className="text-sm text-zinc-500 mt-1">Usa tus credenciales de administrador.</p>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-zinc-600">Correo</Label>
              <Input id="email" type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="login-email-input" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-zinc-600">Contraseña</Label>
              <PasswordInput id="password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="login-password-input" className="mt-1" />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full bg-[#002FA7] hover:bg-[#002277] text-white rounded-md h-11 font-semibold tracking-tight"
          >
            {loading ? "Ingresando…" : "Entrar al sistema →"}
          </Button>
          <p className="text-xs text-zinc-400 font-mono-data text-center">v1.0 · Inventario Gadgets</p>
        </form>
      </div>
    </div>
  );
}
